// src/components/dashboard/GMArtistsPanel.jsx
import React, { useMemo, useState, useEffect } from "react";
import "./gmartistspanel.css";
import * as EnrollmentAPI from "../../util/enrollment_api_util";

/**
 * GM-only: Students + Masters browse/search panel
 *
 * Sort priority (Students strip):
 *  0) Pending signup approval
 *  1) Unread message threads
 *  2) Post-course SUBMITTED needs review
 *  3) Photo package opted in, link still needed
 *  4) Apprenticeship PENDING_REVIEW
 *  5) Apprenticeship CHANGES_REQUESTED
 *  6) Higher pendingCount
 *  7) Alphabetical
 */

const GMArtistsPanel = ({
  studentSearch,
  setStudentSearch,
  masterSearch,
  setMasterSearch,
  filteredStudents = [],
  filteredMasters = [],
  onOpenPerson,
  threads = [],
  pendingApprByStudentId = {},
  photoPackageByStudentId = {},
  socket,
}) => {
  // ✅ Track how many post-course sections are currently SUBMITTED (waiting for staff)
  // { [studentId]: number }
  const [postCoursePendingByStudentId, setPostCoursePendingByStudentId] = useState({});

  useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const res = await EnrollmentAPI.fetchPostCoursePendingSummary();
      const map =
        res?.data?.byStudentId && typeof res.data.byStudentId === "object"
          ? res.data.byStudentId
          : {};

      if (!cancelled) setPostCoursePendingByStudentId(map);
    } catch (e) {
      // non-fatal; sockets will still update live
      console.warn("Could not hydrate post-course pending summary:", e?.message || e);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  if (!socket) return;

  const setCountFromPayload = (payload) => {
    const sid = String(payload?.studentId || "");
    if (!sid) return;

    // ✅ Prefer authoritative count from server
    if (payload?.submittedCount !== undefined && payload?.submittedCount !== null) {
      const n = Math.max(0, Number(payload.submittedCount || 0));
      setPostCoursePendingByStudentId((prev) => {
        const next = { ...(prev || {}) };
        if (n === 0) delete next[sid];
        else next[sid] = n;
        return next;
      });
      return;
    }

    // Fallback (should be rare): old payload without counts
    setPostCoursePendingByStudentId((prev) => {
      const next = { ...(prev || {}) };
      next[sid] = Math.max(0, Number(next[sid] || 0) + 1);
      return next;
    });
  };

  const onPostCourseSubmitted = (payload) => {
    setCountFromPayload(payload);
  };

  const onPostCourseReviewedStaff = (payload) => {
    const sid = String(payload?.studentId || "");
    if (!sid) return;

    // ✅ Prefer authoritative count from server
    if (payload?.submittedCount !== undefined && payload?.submittedCount !== null) {
      const n = Math.max(0, Number(payload.submittedCount || 0));
      setPostCoursePendingByStudentId((prev) => {
        const next = { ...(prev || {}) };
        if (n === 0) delete next[sid];
        else next[sid] = n;
        return next;
      });
      return;
    }

    // Fallback: decrement by 1
    setPostCoursePendingByStudentId((prev) => {
      const next = { ...(prev || {}) };
      const cur = Math.max(0, Number(next[sid] || 0));
      const dec = Math.max(0, cur - 1);
      if (dec === 0) delete next[sid];
      else next[sid] = dec;
      return next;
    });
  };

  socket.on("postcourse:submitted", onPostCourseSubmitted);

  // ✅ Staff dashboards should listen to this
  socket.on("postcourse:reviewed:staff", onPostCourseReviewedStaff);

  // (Optional) If you also emit "postcourse:reviewed" globally somewhere later, keep this too:
  // socket.on("postcourse:reviewed", onPostCourseReviewedStaff);

  return () => {
    socket.off("postcourse:submitted", onPostCourseSubmitted);
    socket.off("postcourse:reviewed:staff", onPostCourseReviewedStaff);
    // socket.off("postcourse:reviewed", onPostCourseReviewedStaff);
  };
}, [socket]);


  const studentNeedsApprenticeshipReview = (appr) => {
    if (!appr?.unlocked) return false;
    const csArr = Array.isArray(appr?.caseStudies) ? appr.caseStudies : [];
    return csArr.some((cs) => {
      const first = String(cs?.firstSession?.status || "").toLowerCase();
      const second = String(cs?.secondSession?.status || "").toLowerCase();
      return first === "pending_review" || second === "pending_review";
    });
  };

  const studentHasApprenticeshipChangesRequested = (appr) => {
    if (!appr?.unlocked) return false;
    const csArr = Array.isArray(appr?.caseStudies) ? appr.caseStudies : [];
    return csArr.some((cs) => {
      const first = String(cs?.firstSession?.status || "").toLowerCase();
      const second = String(cs?.secondSession?.status || "").toLowerCase();
      return first === "changes_requested" || second === "changes_requested";
    });
  };

  const hasUnreadFromPerson = useMemo(() => {
    return (person) => {
      if (!threads || threads.length === 0 || !person) return false;

      const personId = person._id || person.id;
      if (!personId) return false;

      return threads.some((thread) => {
        const student = thread.student;
        const studentId = typeof student === "string" ? student : student?._id || student?.id;
        const unread = thread.unreadCount || 0;
        return studentId && String(studentId) === String(personId) && unread > 0;
      });
    };
  }, [threads]);

  // ✅ Students sorted: pending signup → unread → postcourse submitted → photo package pending → apprenticeship pending → apprenticeship changes → pendingCount → alpha
  const sortedStudents = useMemo(() => {
    const arr = Array.isArray(filteredStudents) ? [...filteredStudents] : [];

    return arr.sort((a, b) => {
      const aId = String(a?._id || a?.id || "");
      const bId = String(b?._id || b?.id || "");

      // 0) pending signup approval FIRST
      const aPendingSignup =
        !!(a?._isPendingStudent || a?.isApproved === false || String(a?.status || "").toLowerCase() === "pending");
      const bPendingSignup =
        !!(b?._isPendingStudent || b?.isApproved === false || String(b?.status || "").toLowerCase() === "pending");
      if (aPendingSignup !== bPendingSignup) return aPendingSignup ? -1 : 1;

      // 1) unread first
      const aUnread = hasUnreadFromPerson(a);
      const bUnread = hasUnreadFromPerson(b);
      if (aUnread !== bUnread) return aUnread ? -1 : 1;

      // 2) post-course submitted needs review
      const aPost = Number(postCoursePendingByStudentId?.[aId] || 0) > 0;
      const bPost = Number(postCoursePendingByStudentId?.[bId] || 0) > 0;
      if (aPost !== bPost) return aPost ? -1 : 1;

      // 3) photo package opted in, but no saved delivery link yet
      const aPhotoPending = !!(photoPackageByStudentId?.[aId]?.hasOptIn && !photoPackageByStudentId?.[aId]?.allReady);
      const bPhotoPending = !!(photoPackageByStudentId?.[bId]?.hasOptIn && !photoPackageByStudentId?.[bId]?.allReady);
      if (aPhotoPending !== bPhotoPending) return aPhotoPending ? -1 : 1;

      // apprenticeship summary objects (as passed from Dashboard)
      const aAppr = pendingApprByStudentId?.[aId];
      const bAppr = pendingApprByStudentId?.[bId];

      // 4) pending_review next
      const aPendingReview = studentNeedsApprenticeshipReview(aAppr);
      const bPendingReview = studentNeedsApprenticeshipReview(bAppr);
      if (aPendingReview !== bPendingReview) return aPendingReview ? -1 : 1;

      // 5) changes_requested next
      const aChanges = studentHasApprenticeshipChangesRequested(aAppr);
      const bChanges = studentHasApprenticeshipChangesRequested(bAppr);
      if (aChanges !== bChanges) return aChanges ? -1 : 1;

      // 6) higher pendingCount next
      const aPendingCount = Number(aAppr?.pendingCount || 0);
      const bPendingCount = Number(bAppr?.pendingCount || 0);
      if (aPendingCount !== bPendingCount) return bPendingCount - aPendingCount;

      // 7) alphabetical
      const aName = `${a?.firstName || a?.first_name || ""} ${a?.lastName || a?.last_name || ""}`
        .trim()
        .toLowerCase();
      const bName = `${b?.firstName || b?.first_name || ""} ${b?.lastName || b?.last_name || ""}`
        .trim()
        .toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [filteredStudents, hasUnreadFromPerson, pendingApprByStudentId, photoPackageByStudentId, postCoursePendingByStudentId]);

  const renderAvatarChip = (person, type) => {
    const id = person._id || person.id;
    const sid = String(id || "");
    const first = person.firstName || person.first_name || "Artist";
    const last = person.lastName || person.last_name || "";
    const avatar = person.profilePic || person.profilePicUrl || person.avatarUrl || person.imageUrl || null;

    const isPendingStudent =
      type === "student" && (person._isPendingStudent || person.isApproved === false || person.status === "pending");
    const isPendingMaster =
      type === "master" && (person._isPendingMaster || person.isApproved === false || person.status === "pending");

    const showPending = isPendingStudent || isPendingMaster;
    const showUnreadDot = type === "student" && hasUnreadFromPerson(person);
    const pendingCount = type === "student" ? Number(pendingApprByStudentId?.[sid]?.pendingCount || 0) : 0;



    const certificationEarned = type === "student" ? !!pendingApprByStudentId?.[sid]?.certificationEarned : false;
    const showPhotoPackageCamera =
      type === "student" ? !!(photoPackageByStudentId?.[sid]?.hasOptIn && !photoPackageByStudentId?.[sid]?.allReady) : false;


    // ✅ Show post-course review pill only when there are SUBMITTED items waiting on staff
    const postCoursePending = type === "student" ? Number(postCoursePendingByStudentId?.[sid] || 0) : 0;
    const showPostCourseReview = postCoursePending > 0;
    const showApprReview = !showPostCourseReview && pendingCount > 0;

    return (
      <button
        key={sid}
        type="button"
        className={`dash-avatar-chip ${type === "master" ? "dash-avatar-chip-master" : "dash-avatar-chip-student"}`}
        onClick={() => {
          // ✅ DO NOT clear review state on click — only clear when staff reviews
          onOpenPerson(person);
        }}
      >
        <div className="dash-avatar-orb dash-avatar-orb-tiny">
          <div className="dash-avatar-face">
            {avatar ? (
              <img src={avatar} alt={first} />
            ) : (
              <span className="dash-avatar-initials">{first.charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div className="dash-avatar-ring" />

          {showUnreadDot && <span className="dash-avatar-unread-dot" />}

          {showPhotoPackageCamera && (
            <span className="dash-avatar-camera-badge" title="Photo package opted in">
              <i className="fa-solid fa-camera" />
            </span>
          )}

          {showPostCourseReview && (
            <span className="dash-attn-pill dash-attn-pill-sm" title="Post-course submissions need review">
              Review{postCoursePending > 1 ? ` (${postCoursePending})` : ""}
            </span>
          )}

      {showApprReview && (
  <span className="dash-attn-pill dash-attn-pill-sm" title="Apprenticeship submissions need review">
    Review
  </span>
)}


          {certificationEarned && (
            <span className="dash-avatar-medal-platinum" title="100-hr Certification Earned">
              🏅
            </span>
          )}
        </div>

        <div className="dash-avatar-chip-text">
          <span className="dash-avatar-chip-first">{first}</span>
          {last && <span className="dash-avatar-chip-last">{last}</span>}
          {showPending && <span className="dash-badge-pill dash-badge-pill-warn">Pending</span>}
        </div>
      </button>
    );
  };

  return (
    <div className="dash-panel dash-artists-panel">
      <h2 className="dash-panel-title">Artists</h2>
      <p className="dash-panel-subtitle">
        Browse COSMETINK students and masters. Tap an avatar to open their profile, assign prep courses, and approve them.
      </p>

      <div className="dash-section">
        <div className="dash-section-label">Students</div>
        <input
          type="text"
          className="dash-search-input"
          placeholder="Search students by name or email…"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
        />
        <div className="dash-avatar-strip">
          {sortedStudents.length === 0 ? (
            <p className="dash-empty-text">No matching students. Try a different name or email.</p>
          ) : (
            sortedStudents.map((s) => renderAvatarChip(s, "student"))
          )}
        </div>
      </div>

      <div className="dash-section dash-section-divider hidden">
        <div className="dash-section-label">Masters</div>
        <input
          type="text"
          className="dash-search-input"
          placeholder="Search masters by name or email…"
          value={masterSearch}
          onChange={(e) => setMasterSearch(e.target.value)}
        />
        <div className="dash-avatar-strip">
          {filteredMasters.length === 0 ? (
            <p className="dash-empty-text">No matching masters. Try a different name or email.</p>
          ) : (
            filteredMasters.map((m) => renderAvatarChip(m, "master"))
          )}
        </div>
      </div>
    </div>
  );
};

export default GMArtistsPanel;
